import { ReportTemplateData } from '../steps/notes/utils/notesTemplates.util';
import { LIMITATIONS_DISCLOSURES_HTML } from '../steps/notes/constants/limitations';
import {
  buildCoverPageHTML,
  buildExecutiveSummaryHTML,
  buildAllReductionBlocksHtml,
  buildReductionCommentsHtml,
  buildReductionTableHtml,
  buildProjectDescriptionHtml,
  ExecSummaryReductions,
  DrawReductions,
} from '../steps/notes/utils/notesTemplates.util';
import { drawScopedKey } from '../../magic-report/sections/draw-scoped-sections.util';
import { sortLienWaiverHistory } from '../../magic-report/sections/lien-waivers-draws.util';
import { filterVisibleCoLienDraws, resolveHiddenCoLienDrawIds } from '../../magic-report/sections/co-lien-visibility.util';
import { buildMagicReportCoverPageHTML, CoverPageTableData } from '../steps/notes/utils/coverPageTemplates.util';
import { parseISO } from 'date-fns';
import { capitalize, formatDateToMMDDYY } from '../../../../common/utils/common.util';
import { REDUCTION_DIFFERENCE_THRESHOLD } from '../../../../common/constants/general';
import logoBase64 from '../../../../assets/img/logo_with_text_base64';
import { ReportPhotoDTO } from '../photos/types';
import { stripSignatureInlineSize } from './signature.util';
import { isChangedFromLastPeriod } from '../../../project/project.util';
import {
  buildNotarySignaturesHtml,
  buildChangeOrdersHtml,
  buildPotentialChangeOrdersHtml,
  buildReallocationsHtml,
  buildRetainageHtml,
  buildStoredMaterialsHtml,
  buildLienWaiversHtml,
  buildConstructionScheduleHtml,
  buildConstructionMeetingMinutesHtml,
  buildMaterialTestingHtml,
  buildPreviousFundingsTextHtml,
  buildDocumentsReceivedHtml,
  buildFoundationSurveyHtml,
  buildInitialPcrIssuesHtml,
  buildMinorAdjustmentsHtml,
  buildDynamicSectionsHtml,
  buildProgressDescriptionHtml,
  buildReportQualificationsPart1Html,
  buildReportQualificationsPart2Html,
} from '../../magic-report/sections/magic-report-section-export.util';

// ─── Public Interfaces ────────────────────────────────────────────────────────

/**
 * One covered draw as the export sees it. Mirrors `DrawContext` from the wizard but with the same
 * loose typing the rest of this file uses for budget/line-item data.
 */
export interface ExportDrawContext {
  drawId?: string;
  drawSummary?: any;
  budgetSummary?: any;
  lineItems?: any[];
}

export interface ExportReportData extends ReportTemplateData {
  lineItems?: any[];
  reportId?: string;
  coverPagePhotoIds?: string[];
  sections?: any;
  coverPage?: any;
  /**
   * Every draw the report covers, ordered oldest application first. Printed artifacts that are
   * per-draw (budget pages, G703 continuation sheets, the §2.x application sections, and the §1.1
   * reduction blocks) iterate this. The inherited singular `drawSummary`/`budgetSummary`/`lineItems`
   * mirror the primary (last) draw and still drive the blocks not yet migrated.
   */
  draws?: ExportDrawContext[];
  /**
   * Covered draws whose Change Orders and Lien Waivers blocks are suppressed. Those two sections
   * skip these draws entirely — no table, no summary, no history row, and nothing of theirs in
   * the sections' combined comments. Every other per-draw artifact still prints all draws.
   */
  hiddenCoLienDrawIds?: string[];
  /**
   * Recap of Previous Applications for every draw in the project (from the Excel Report tab, then one
   * row per draw logged at each reset). Printed as-is in §3.1 — never derived from previous fundings.
   */
  recapApplications?: any[];
}

export interface ExportSummary {
  coverLetter?: string;
  executiveSummary?: string;
  actionItems?: string;
}

export interface ExportPhotosFunction {
  (reportId: string): Promise<any[]>;
}

export interface ExportImageUrlFunction {
  (blobName: string): Promise<string>;
}

export interface ExportSignatureFunction {
  (): Promise<{ dataUrl: string }>;
}

// ─── Formatting Helpers ───────────────────────────────────────────────────────

function formatNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatCurrencyUSD(value: any): string {
  const num = formatNumber(value);
  if (num === null) return '--';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return num < 0 ? `(${formatted})` : `$${formatted}`;
}

function formatCurrencyNoSign(value: any): string {
  const num = formatNumber(value);
  if (num === null) return '--';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return num < 0 ? `(${formatted})` : formatted;
}

function formatPercent(value: any): string {
  const num = formatNumber(value);
  if (num === null) return '--';
  return `${Number(num).toFixed(2)}%`;
}

function formatIntegerNoSign(value: any): string {
  const num = formatNumber(value);
  if (num === null) return '--';
  return Math.round(num).toLocaleString('en-US');
}

function formatDateLong(value: any): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateShortOrEmpty(value: any): string {
  if (value === null || value === undefined || value === '' || value === 0 || value === '0') return '';
  return formatDateToMMDDYY(value);
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const REPORT_STYLES = `
  @page { size: A4 portrait; }
  @page landscape-page { size: A4 landscape; margin: 19.05mm 25.4mm 25.4mm 25.4mm; }
  @page photos { size: A4; }
  html, body { background: white; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { break-after: page; background: white; margin: 0; padding: 0; box-sizing: border-box; }
  .page:last-child { break-after: avoid; }
  .MuiPaper-root { box-sizing: border-box !important; background: white !important; margin: 0 auto !important; display: block !important; box-shadow: none !important; border-radius: 0 !important; font-family: "Times New Roman", serif !important; font-size: 20px !important; }

  /* Cover letter */
  .page.cover-letter img.company-logo,
  .page.cover-letter img[alt="CD Construction Consulting"] { width: 280px !important; max-width: 280px !important; height: auto !important; display: block !important; margin: -27px auto 2rem auto !important; }
  /* Signature is sized in mm (like the @page boxes) so it prints at least 1in tall whatever scale the PDF renderer uses. */
  .page.cover-letter img[alt="Signature"] { height: 28mm !important; width: auto !important; max-width: 90mm !important; max-height: 28mm !important; display: block !important; }
  .page.cover-letter .signature-company { margin: 0 !important; }
  .page.cover-letter .MuiPaper-root p:last-of-type { margin: 0 !important; }
  .page.cover-letter .MuiPaper-root p { text-align: justify; }

  /* Executive summary */
  .page.executive-summary .exec-summary { font-family: Times New Roman, serif; line-height: 1.4; }
  .page.executive-summary .exec-summary p { margin: 0.3em 0; white-space: pre-wrap; min-height: 1.2em; font-size: 23px !important; text-align: justify; }
  .page.executive-summary .exec-summary h2 { color: #1976d2; font-weight: bold; margin-bottom: 1rem; font-size: 25px !important; }
  .page.executive-summary .exec-summary h3 { font-weight: bold; margin-bottom: 0.5rem; font-size: 20px !important; }
  .page.executive-summary .exec-summary img.company-logo,
  .page.executive-summary .exec-summary img[alt="CD Construction Consulting"] { width: 280px !important; max-width: 280px !important; height: auto !important; display: block !important; margin: -27px auto 2rem auto !important; }

  /* Action items */
  .page.action-items .action-items,
  .page.executive-summary .action-items,
  .page.section-page .action-items { font-family: Times New Roman, serif; line-height: 1.4; }
  .page.action-items .action-items p,
  .page.executive-summary .action-items p,
  .page.section-page .action-items p { margin: 0.3em 0; white-space: pre-wrap; min-height: 1.2em; font-size: 23px !important; text-align: justify; }
  .page.action-items .action-items h2,
  .page.executive-summary .action-items h2,
  .page.section-page .action-items h2 { color: #1976d2; font-weight: bold; margin-bottom: 1rem; font-size: 25px !important; }
  .page.action-items .action-items h3,
  .page.executive-summary .action-items h3,
  .page.section-page .action-items h3 { font-weight: bold; margin-bottom: 0.5rem; font-size: 23px !important; }
  .page.action-items .action-items ol,
  .page.executive-summary .action-items ol,
  .page.section-page .action-items ol { list-style: none; padding-left: 0; margin-left: 0; }
  .page.action-items .action-items ol li,
  .page.executive-summary .action-items ol li,
  .page.section-page .action-items ol li { position: relative; padding-left: 4em; margin-bottom: 0.8em; counter-increment: action-num; font-size: 23px; }
  .page.action-items .action-items ol li::before,
  .page.executive-summary .action-items ol li::before,
  .page.section-page .action-items ol li::before { content: "1.2." counter(action-num) "."; position: absolute; left: 0; top: 0; font-size: 23px; white-space: nowrap; }

  /* Recap */
  .page.recap .recap-subtitle { font-weight: bold; font-size: 23px; margin: 10px 0 6px 0; }
  .page.recap .recap-table th { background: none; font-weight: bold; font-size: 16px; text-align: left; border: none !important; }
  .page.recap .recap-table td { font-size: 16px; border: none !important; }
  .page.recap .recap-previous-fundings { margin-bottom: 16px; }
  .page.recap .recap-previous-fundings th { background: none; font-weight: bold; font-size: 16px; text-align: left; border: none !important; }
  .page.recap .recap-previous-fundings td { font-size: 16px; border: none !important; }
  .page.recap .recap-empty { text-align: center; font-style: italic; }

  /* Limitations */
  .page.limitations .limitations-content { font-family: Times New Roman, serif !important; font-size: 20px !important; line-height: 1.4 !important; }
  .page.limitations .limitations-content p { margin: 2em 0 !important; font-size: 20px !important; font-family: "Times New Roman", serif !important; }
  .page.limitations .limitations-content h2 { color: #1976d2 !important; font-weight: bold !important; margin-bottom: 1rem !important; font-size: 25px !important; }

  /* Section pages */
  .major-section-header { color: #1976d2; font-weight: bold; font-size: 25px; margin: 0 0 16px 0; }
  .section-subheader { font-weight: bold; font-size: 23px; margin: 0 0 10px 0; }
  .page.section-page { font-family: "Times New Roman", serif; font-size: 20px; line-height: 1.4; }
  .page.section-page p { margin: 0.3em 0; font-size: 20px; text-align: justify; }
  .page.report-qualifications { font-family: "Times New Roman", serif; font-size: 20px; line-height: 1.4; }
  .page.report-qualifications .section-header { color: #1976d2; font-weight: bold; font-size: 25px; margin: 0 0 12px 0; }
  .page.report-qualifications p { margin: 0.3em 0; font-size: 23px; text-align: justify; }
  .page.photos { page: photos !important; padding: 0 !important; min-height: calc(297mm - 101px) !important; }
  .section-header { color: #1976d2; font-weight: bold; font-size: 25px; margin: 0 0 10px 0; }

  /* Layout */
  .two-col { display: flex; align-items: flex-start; gap: 20px; margin-top: 16px; }
  .two-col > div { width: 50%; }

  /* Tables */
  .table { width: 100%; border-collapse: collapse; }
  .table td { border: 1px solid #ccc; padding: 6px; font-size: 16px; }
  .table th { border: 1px solid #ccc; padding: 6px; font-size: 16px; }
  .section-page .table.budget-summary { width: 65%; }

  /* Single-spaced rows for every portrait-page table (recap, previous fundings, lien waivers,
     reallocations, change orders, schedule, material testing, docs received, ...). These tables are
     built with inline "padding: 4px 8px" in the section builders; !important outranks that inline
     value, so the vertical padding collapses without touching each builder. The landscape G702/G703
     pages and the cover page keep their own tuned spacing and are excluded. */
  .page:not(.landscape):not(.magic-cover-page) table td,
  .page:not(.landscape):not(.magic-cover-page) table th { padding-top: 0 !important; padding-bottom: 0 !important; line-height: 1.2 !important; }

  /* Section page inline tables (non-G703): centered with side margins */
  .page.section-page table:not(.g703-table) { margin-left: auto !important; margin-right: auto !important; max-width: calc(100% - 40px) !important; }
  .page.report-qualifications table:not(.g703-table) { margin-left: auto !important; margin-right: auto !important; max-width: calc(100% - 40px) !important; }
  .table.draw-summary td, .table.draw-summary th { border: none; padding: 1px 6px; font-size: 14px; }
  .table.draw-summary tr td:first-child { width: 15%; }

  /* Budget summary */
  .table.budget-summary td { border: none; padding: 0; }
  .bs-highlighted td { border: 1px solid #000 !important; }
  .bs-label-hl { font-weight: bold; font-size: 20px; padding: 6px 8px !important; }
  .bs-value-hl { background: #FFFECF; font-weight: bold; font-size: 20px; text-align: right; padding: 6px 8px !important; }
  .bs-regular td { border-top: none !important; border-bottom: none !important; border-left: 1px solid #000 !important; border-right: 1px solid #000 !important; }
  .bs-label { font-size: 16px; padding: 5px 8px !important; }
  .bs-value { background: #FFFECF; font-size: 16px; text-align: right; padding: 5px 8px !important; }
  .bs-sub td { border-top: none !important; border-bottom: none !important; border-left: 1px solid #000 !important; border-right: 1px solid #000 !important; }
  .bs-sub:last-child td { border-bottom: 1px solid #000 !important; }
  .bs-sub-label { font-size: 16px; padding: 3px 8px !important; text-align: left; }
  .bs-sub-value { background: #FFFECF; font-size: 16px; text-align: right; padding: 3px 8px !important; }
  .bs-value.negative, .bs-value-hl.negative, .bs-sub-value.negative { color: red; }
  /* Application for Payment Review Form page only — section 2.1 renders the same table and keeps the generic .table td size. */
  .page.budget .bs-label-hl, .page.budget .bs-value-hl { font-size: 13px; }
  .page.budget .bs-label, .page.budget .bs-value, .page.budget .bs-sub-label, .page.budget .bs-sub-value { font-size: 12px; }

  /* Line items continuation (G703)
     Column widths are fixed (see the table's colgroup) so Descriptions can be truncated instead of
     wrapped, and the yellow column shading mirrors ProjectTable (REPORT_COLORS.YELLOW / DARK_YELLOW). */
  .line-items { table-layout: fixed; }
  /* !important beats the editor iframe's global ".page *" wrap rule (overflow-wrap: anywhere),
     which otherwise breaks header words mid-letter ("Applicati / on"). */
  .line-items thead th { background: #FFF59D; font-weight: bold; font-size: 10px; line-height: 1.1; text-align: left; padding: 0 8px; border: 1px solid #ccc; word-break: normal !important; overflow-wrap: normal !important; hyphens: none !important; }
  .line-items td { font-size: 12px; line-height: 1.15; padding: 0 8px; text-align: right; border-top: none; border-bottom: 0.5px solid rgba(204, 204, 204, 0.4); border-left: 1px solid #ccc; border-right: 1px solid #ccc; }
  .line-items td:first-child { text-align: left; white-space: nowrap !important; overflow: hidden; text-overflow: ellipsis; word-break: normal !important; overflow-wrap: normal !important; }
  /* Calculated / CDCC-entered columns — same shading as the project line-items table */
  .line-items tbody tr:not(.totals-row) td:nth-child(5),
  .line-items tbody tr:not(.totals-row) td:nth-child(10),
  .line-items tbody tr:not(.totals-row) td:nth-child(11),
  .line-items tbody tr:not(.totals-row) td:nth-child(12) { background: #FFFECF; }
  .line-items tbody tr:not(.totals-row) td:nth-child(9),
  .line-items tbody tr:not(.totals-row) td:nth-child(13) { background: #FFF59D; }
  .line-items tr.totals-row td { border: 1px solid #ccc; padding: 0 8px; }
  .line-items tr.totals-row { break-inside: avoid; page-break-inside: avoid; break-before: avoid; page-break-before: avoid; }
  .line-items tr.page-break-row td { border-bottom: 1px solid #ccc; }
  .budget-title, .continuation-title { color: #1976d2; font-weight: bold; font-size: 18px; margin: 0 0 10px; }

  /* Previous fundings */
  .pf-box { border: 1px solid #000; width: 100%; min-height: 368px; display: flex; flex-direction: column; }
  .previous-fundings { table-layout: fixed; border-collapse: collapse; flex: 0 0 auto; }
  .previous-fundings td, .previous-fundings th { border: none; padding: 0; }
  .pf-totals-table { table-layout: fixed; border-collapse: collapse; flex: 1 1 auto; }
  .pf-filler-row, .pf-filler-row td { height: 100%; }
  .pf-header-main { background: white; color: #000; font-weight: bold; font-size: 13px; padding: 5px 6px !important; border-bottom: 1px solid #000 !important; text-align: left; }
  .pf-subheader { font-size: 12px; font-weight: normal; padding: 2px 6px !important; border-bottom: 1px solid #777 !important; text-align: left; }
  .pf-subheader-appno { border-right: 1px solid #000 !important; text-align: center !important; }
  .pf-subheader-right { text-align: right !important; }
  .pf-cell { font-size: 12px; padding: 2px 6px !important; }
  .pf-cell-appno { font-size: 12px; padding: 2px 6px !important; border-right: 1px solid #000 !important; text-align: center !important; }
  .pf-cell-right { text-align: right; }
  .pf-cell-center { text-align: center; }
  .pf-totals-appno { border-right: 1px solid #000 !important; }
  .pf-totals-label { background: white; font-weight: bold; font-size: 12px; padding: 3px 6px !important; border-top: 1px solid #000 !important; text-align: left !important; }
  .pf-totals-blank { background: #FFFECF; border-top: 1px solid #000 !important; }
  .pf-totals-value { background: #FFFECF; font-weight: bold; font-size: 12px; padding: 3px 6px !important; border-top: 1px solid #000 !important; text-align: right; }
  .pf-asterisk { font-weight: bold; }
  .pf-footnote { font-size: 13px; font-style: italic; padding: 3px 6px; border-top: 1px solid #000; }

  /* Photos */
  .photos-header { margin: 0 0 32px 0; }
  .photos-header-line1 { font-size: 16px; font-weight: bold; color: #000; text-align: center; margin: 0 0 4px 0; }
  .photos-header-line2 { color: #1976d2; font-weight: bold; font-size: 25px; text-align: center; }
  .photos-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 10px; row-gap: 20px; }
  .photo { display: flex; flex-direction: column; align-items: center; }
  .photo .image-frame { height: 322px; width: 450px; display: flex; align-items: center; justify-content: center; }
  .photo img { width: 450px; height: 322px; object-fit: cover; display: block;}
  .photo .caption { min-height: 72px; width: 100%; font-size: 20px; color: #333; display: flex; align-items: center; justify-content: center; text-align: center; padding: 0 10px; box-sizing: border-box; }
  .photo .caption * { text-align: center; }

  /* Magic cover page */
  .magic-cover-page { font-family: "Times New Roman", serif !important; page-break-inside: avoid !important; break-inside: avoid !important; }
  .magic-cover-page .magic-report-cover-page { font-family: "Times New Roman", serif !important; page-break-inside: avoid !important; break-inside: avoid !important; }
  /* The source logo PNG has ~18% transparent padding baked in above/below the mark, so the
     visible box is cropped to the actual artwork via negative margin + overflow:hidden on the wrapper */
  .magic-cover-page .magic-report-cover-page .company-logo-wrap { height: 90px !important; overflow: hidden !important; }
  .magic-cover-page .magic-report-cover-page img.company-logo,
  .magic-cover-page .magic-report-cover-page img[alt="CD Construction Consulting"] { height: 140px !important; width: auto !important; max-width: none !important; display: block !important; margin: -25px 0 0 0 !important; padding: 0 !important; vertical-align: top !important; }
  .magic-cover-page .magic-cover-page-table { margin-top: 5px !important; padding: 0 10px !important; page-break-inside: avoid !important; break-inside: avoid !important; }
  .magic-cover-page .magic-cover-page-table * { box-sizing: border-box !important; }
  .magic-cover-page svg { page-break-inside: avoid !important; break-inside: avoid !important; }
  .magic-cover-page .cover-page-graphs { page-break-inside: avoid !important; break-inside: avoid !important; }

  /* Global page-break rules */
  h1, h2, h3, h4, .section-header, .major-section-header, .section-subheader, .budget-title, .continuation-title, .photos-header, .recap-subtitle, .italic-subheader { break-after: avoid; page-break-after: avoid; }
  h1 + *, h2 + *, h3 + *, h4 + *, .section-header + *, .major-section-header + *, .section-subheader + *, .budget-title + *, .continuation-title + *, .photos-header + *, .recap-subtitle + *, .italic-subheader + * { break-before: avoid; page-break-before: avoid; }
`;

// ─── Page Builders ────────────────────────────────────────────────────────────

function buildPage(className: string, innerHtml: string): string {
  return `<div class="page ${className}">${innerHtml}</div>`;
}

function buildLandscapePage(className: string, innerHtml: string): string {
  return `<div class="page landscape ${className}" style="page: landscape-page;">${innerHtml}</div>`;
}

function buildContinuationPage(rows: string[], lineItemsTableHead: string, lineItemsTotalsRow: string): string {
  return buildLandscapePage(
    'continuation',
    `<div class="continuation-title">CDCC - APPLICATION FOR PAYMENT REVIEW FORM - CONTINUATION SHEET</div>
     <table class="table line-items">
       ${LINE_ITEMS_COLGROUP}
       ${lineItemsTableHead}
       <tbody>
         ${rows.join('')}
         ${lineItemsTotalsRow}
       </tbody>
     </table>`,
  );
}

function buildPhotosPages(processedPhotos: any[], photosPerPage: number, headerLine1: string): string {
  return processedPhotos
    .reduce((pages: string[], _, i) => {
      if (i % photosPerPage !== 0) return pages;
      const pagePhotos = processedPhotos.slice(i, i + photosPerPage);
      const grid = pagePhotos
        .map(
          (p: any) =>
            `<div class="photo"><div class="image-frame"><img src="${p.url}" /></div><div class="caption">${p.caption ?? ''}</div></div>`,
        )
        .join('');
      const header =
        i === 0
          ? `<div class="photos-header"><div class="photos-header-line1">${headerLine1}</div><div class="photos-header-line2">PHOTOGRAPHS</div></div>`
          : '';
      pages.push(buildPage('photos', `${header}<div class="photos-grid">${grid}</div>`));
      return pages;
    }, [])
    .join('');
}

/** "{projectName}, {date}" — the top line of the photos page header box. */
function buildPhotosHeaderLine1(projectName: string | undefined, date: any): string {
  const parsedDate = date ? (typeof date === 'string' ? parseISO(date) : new Date(date)) : null;
  const formattedDate =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '';
  return projectName && formattedDate ? `${projectName}, ${formattedDate}` : projectName || formattedDate || '';
}

// ─── Recap Table Builders ─────────────────────────────────────────────────────

/** One G702's table of the Recap of Previous Applications, laid out as on the Excel Report tab. */
function buildRecapApplicationsTable(heading: string, rows: string): string {
  return `
    <div class="recap-subtitle">${heading}</div>
    <table class="table recap-previous-fundings">
      <thead>
        <tr>
          <th>Appl. #</th>
          <th>Date:</th>
          <th>Requested:</th>
          <th>Approved:</th>
          <th>Retainage:</th>
          <th>% Requested:</th>
          <th>% Complete:</th>
          <th>Contract Amount:</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="8" class="recap-empty">No previous applications recorded.</td></tr>`}
      </tbody>
    </table>
  `;
}

function buildRecapTable(applicationsTables: string, recapRows: string, lienWaiversHeading: string, hasConditional: boolean): string {
  const lienWaiversTable = `
    <div class="recap-subtitle">${lienWaiversHeading}</div>
    <table class="table recap-table">
      <thead>
        <tr>
          <th>Contractor</th>
          <th>Through Date</th>
          <th>Application #</th>
          <th>${hasConditional ? 'Conditional / Amount' : 'Amount'}</th>
        </tr>
      </thead>
      <tbody>
        ${recapRows || `<tr><td colspan="4" class="recap-empty">No lien waivers received for previous month.</td></tr>`}
      </tbody>
    </table>
  `;

  return `${applicationsTables}${lienWaiversTable}`;
}

// ─── Budget Table Builders ────────────────────────────────────────────────────

function buildBudgetSummaryTable(budget: any): string {
  const isNegative = (val: any) => {
    const n = Number(val);
    return Number.isFinite(n) && n < 0;
  };

  const retainagePct = formatPercent(budget?.totalRetainageHeldPercentage);
  const retainageAmt = formatCurrencyNoSign(budget?.totalRetainageHeld);

  const highlightedRow = (label: string, value: string, negative = false) => `
    <tr class="bs-highlighted">
      <td class="bs-label-hl">${label.toUpperCase()}</td>
      <td class="bs-value-hl${negative ? ' negative' : ''}">${value}</td>
    </tr>`;

  const regularRow = (label: string, value: string, negative = false) => `
    <tr class="bs-regular">
      <td class="bs-label">${label}</td>
      <td class="bs-value${negative ? ' negative' : ''}">${value}</td>
    </tr>`;

  const retainageRow = () => `
    <tr class="bs-regular">
      <td class="bs-label">Total Retainage Held:<span style="float:right; padding-right:8px;">${retainagePct}</span></td>
      <td class="bs-value${isNegative(budget?.totalRetainageHeld) ? ' negative' : ''}">${retainageAmt}</td>
    </tr>`;

  const subRow = (label: string, value: string, negative = false) => `
    <tr class="bs-sub">
      <td class="bs-sub-label">${label}</td>
      <td class="bs-sub-value${negative ? ' negative' : ''}">${value}</td>
    </tr>`;

  return `
    <table class="table budget-summary">
      <colgroup><col style="width:60%"/><col style="width:40%"/></colgroup>
      <tbody>
        ${highlightedRow('Original Contract / Budget Sum:', formatCurrencyNoSign(budget?.originalContractBudgetSum))}
        ${regularRow('Reallocations / Change Orders:', formatCurrencyNoSign(budget?.reallocationsChangeOrders))}
        ${highlightedRow('Contract / Budget Sum To Date:', formatCurrencyNoSign(budget?.contractBudgetSumToDate))}
        ${regularRow('Total Completed and Stored:', formatCurrencyNoSign(budget?.totalCompletedAndStored))}
        ${retainageRow()}
        ${regularRow('Total Earned Less Retainage:', formatCurrencyNoSign(budget?.totalEarnedLessRetainage))}
        ${regularRow('Less Previous Fundings:', formatCurrencyNoSign(budget?.lessPreviousFundings))}
        ${regularRow('Current Payment Due:', formatCurrencyNoSign(budget?.currentPaymentDue))}
        ${regularRow('Balance to Complete Plus Retainage:', formatCurrencyNoSign(budget?.balanceToCompletePlusRetainage))}
        ${regularRow('Adjustments by CDCC:', formatCurrencyNoSign(budget?.adjustmentsByCDCC))}
        ${highlightedRow(
          'Total Amount Recommended For Funding:',
          formatCurrencyNoSign(budget?.totalAmountReccForFunding),
          isNegative(budget?.totalAmountReccForFunding),
        )}
        ${subRow('Requested Amount', formatCurrencyNoSign(budget?.requestedAmount), isNegative(budget?.requestedAmount))}
        ${subRow('Difference', formatCurrencyNoSign(budget?.difference), isNegative(budget?.difference))}
        ${subRow('Total Contract / Budget % Complete', formatPercent(budget?.totalContractBudgetPerComplete))}
      </tbody>
    </table>
  `;
}

function buildDrawSummaryTable(project: any, draw: any): string {
  const address =
    [
      project?.projectAddress?.street,
      capitalize(project?.projectAddress?.city) || null,
      (project?.projectAddress?.state || '').toUpperCase(),
      project?.projectAddress?.postalCode,
    ]
      .filter(Boolean)
      .join(', ') || '--';

  return `
    <table class="table draw-summary">
      <colgroup>
        <col style="width:15%" />
        <col />
      </colgroup>
      <tbody>
        <tr><td>Project Title:</td><td><strong>${project?.projectTitle ?? '--'}</strong></td></tr>
        <tr><td>Project #:</td><td><strong>${project?.projectNumber ?? '--'}</strong></td></tr>
        <tr><td>Project Address:</td><td><strong>${address}</strong></td></tr>
        <tr><td>Contractor:</td><td><strong>${project?.contractor ?? '--'}</strong></td></tr>
        <tr><td>Application #:</td><td><strong>${draw?.applicationNumber || '--'}</strong></td></tr>
        <tr><td>Period Beginning:</td><td><strong>${formatDateLong(draw?.periodBeginning)}</strong></td></tr>
        <tr><td>Period Ending:</td><td><strong>${formatDateLong(draw?.periodEnding)}</strong></td></tr>
      </tbody>
    </table>
  `;
}

function buildPreviousFundingsTable(
  rows: string[],
  showTotals: boolean,
  totalAmount: number,
  tableHead: string,
  hasUnreviewedFunding = false,
): string {
  const colgroup = `<colgroup><col style="width:34%;" /><col style="width:33%;" /><col style="width:33%;" /></colgroup>`;

  return `
    <div class="pf-box">
      <table class="previous-fundings" style="width:100%; table-layout:fixed;">
        ${colgroup}
        ${tableHead}
        <tbody>
          ${rows.join('')}
        </tbody>
      </table>
      ${
        showTotals
          ? `
        <table class="pf-totals-table" style="width:100%; height:100%; table-layout:fixed;">
          ${colgroup}
          <tbody>
            <tr class="pf-filler-row">
              <td class="pf-cell-appno"></td>
              <td class="pf-cell"></td>
              <td class="pf-cell"></td>
            </tr>
            <tr class="pf-totals">
              <td class="pf-totals-label pf-totals-appno">TOTALS:</td>
              <td class="pf-totals-blank"></td>
              <td class="pf-totals-value">${formatCurrencyNoSign(totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      `
          : ''
      }
      ${hasUnreviewedFunding ? `<div class="pf-footnote">*&nbsp; Not reviewed by CDCC</div>` : ''}
    </div>
  `;
}

// ─── Line Items Builders ──────────────────────────────────────────────────────

/** Fixed column widths for the G703 table: a wide Description (truncated, never wrapped) + 12 even numeric columns. */
const LINE_ITEMS_COLGROUP = `
  <colgroup>
    <col style="width:18%" />
    ${Array(12).fill('<col style="width:6.83%" />').join('')}
  </colgroup>
`;

const LINE_ITEMS_TABLE_HEAD = `
  <thead>
    <tr>
      <th>Description</th>
      <th>Original Budget</th>
      <th>Reallocation</th>
      <th>Change Order</th>
      <th>Revised Budget</th>
      <th>Previous Application</th>
      <th>Complete This Period</th>
      <th>Stored This Period</th>
      <th>CDCC Approved</th>
      <th>Completed Stored To Date</th>
      <th>% Complete</th>
      <th>Balance To Complete</th>
      <th>Retainage</th>
    </tr>
  </thead>
`;

// Mirrors ProjectTable's bolding of Reallocation / Change Order cells that changed since last period.
const periodChangeBoldAttr = (item: any, field: string) => (isChangedFromLastPeriod(item, field) ? ' style="font-weight:bold;"' : '');

function buildLineItemRows(lineItems: any[]): string[] {
  return lineItems.map(
    (item: any) => `
    <tr>
      <td>${item.lineItem?.description ?? '--'}</td>
      <td>${formatIntegerNoSign(item.originalBudget)}</td>
      <td${periodChangeBoldAttr(item, 'reallocation')}>${formatIntegerNoSign(item.reallocation)}</td>
      <td${periodChangeBoldAttr(item, 'changeOrder')}>${formatIntegerNoSign(item.changeOrder)}</td>
      <td>${formatIntegerNoSign(item.revisedBudget)}</td>
      <td>${formatIntegerNoSign(item.previousApplication)}</td>
      <td>${formatIntegerNoSign(item.completeThisPeriod)}</td>
      <td>${formatIntegerNoSign(item.storedThisPeriod)}</td>
      <td>${formatIntegerNoSign(item.cdccApproved)}</td>
      <td>${formatIntegerNoSign(item.completedStoredToDate)}</td>
      <td>${formatPercent(item.percentageComplete)}</td>
      <td>${formatIntegerNoSign(item.balanceToComplete)}</td>
      <td>${formatIntegerNoSign(item.retainage)}</td>
    </tr>
  `,
  );
}

function buildLineItemTotalsRow(lineItems: any[]): string {
  const sum = (key: string) => lineItems.reduce((acc: number, it: any) => acc + (Number(it[key]) || 0), 0);
  return `
    <tr class="totals-row">
      <td style="background:#FFFECF; font-weight:bold;">TOTALS</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('originalBudget'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('reallocation'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('changeOrder'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('revisedBudget'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('previousApplication'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('completeThisPeriod'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('storedThisPeriod'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('cdccApproved'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('completedStoredToDate'))}</td>
      <td style="background:#FFFECF;"></td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('balanceToComplete'))}</td>
      <td style="background:#FFFECF; font-weight:bold;">${formatIntegerNoSign(sum('retainage'))}</td>
    </tr>
  `;
}

// ─── Budget + Continuation Page Assembly ─────────────────────────────────────

interface BudgetPagesContext {
  budget: any;
  draw: any;
  project: any;
  lineItems: any[];
  budgetSummaryTable: string;
  drawSummaryTable: string;
  previousFundingRowItems: string[];
  previousFundingsTotal: number;
  previousFundingsTableHead: string;
  hasUnreviewedFunding: boolean;
  lineItemRows: string[];
  lineItemTotalsRow: string;
}

function buildBudgetPages(ctx: BudgetPagesContext): string[] {
  const {
    budget,
    lineItems,
    budgetSummaryTable,
    drawSummaryTable,
    previousFundingRowItems,
    previousFundingsTotal,
    previousFundingsTableHead,
    hasUnreviewedFunding,
    lineItemRows,
    lineItemTotalsRow,
  } = ctx;

  if (!budget && lineItems.length === 0) return [];

  const pages: string[] = [];
  const pfTable = (rows: string[], showTotals: boolean) =>
    buildPreviousFundingsTable(rows, showTotals, previousFundingsTotal, previousFundingsTableHead, hasUnreviewedFunding);

  // Single budget page with all previous fundings rows.
  pages.push(
    buildLandscapePage(
      'budget',
      `<div class="budget-title">CDCC - APPLICATION FOR PAYMENT REVIEW FORM</div>
       ${drawSummaryTable}
       <div class="two-col">
         <div>${pfTable(previousFundingRowItems, true)}</div>
         <div>${budgetSummaryTable}</div>
       </div>`,
    ),
  );

  // Continuation sheet
  if (lineItemRows.length > 0) {
    pages.push(buildContinuationPage(lineItemRows, LINE_ITEMS_TABLE_HEAD, lineItemTotalsRow));
  }

  return pages;
}

// ─── Magic Report Section Assembly ───────────────────────────────────────────

/**
 * Extracts one draw's reductions from its reduction-comments section and budget.
 *
 * Returns undefined when that draw has nothing to report, i.e. neither auto-detected reduction
 * rows nor a materially negative difference. Manual-only reductions (negative difference but no
 * auto rows) keep `rows` empty, which suppresses the line-item table downstream.
 */
function extractDrawReductions(
  reductionSection: any,
  budgetSummary: any,
  reportType: 'limited' | 'quarterly' | 'magic',
): ExecSummaryReductions | undefined {
  if (reportType !== 'magic') return undefined;

  const reductionRowsField = reductionSection?.fields?.find((f: any) => f.fieldName === 'ReductionCommentsRows');
  const hasAutoReductionRows = ((reductionRowsField?.entries as any[]) || []).some((e: any) => {
    try {
      const parsed = JSON.parse(e.value || '');
      return parsed && typeof parsed === 'object';
    } catch {
      return false;
    }
  });

  const difference = Number(budgetSummary?.difference ?? 0);
  const hasDifferenceReduction = difference < 0 && Math.abs(difference) >= REDUCTION_DIFFERENCE_THRESHOLD;
  if (!hasAutoReductionRows && !hasDifferenceReduction) return undefined;

  const zeroWorkField = reductionSection?.fields?.find((f: any) => f.fieldName === 'ZeroWorkComments');
  const partialField = reductionSection?.fields?.find((f: any) => f.fieldName === 'PartialReductionComments');
  const rows = hasAutoReductionRows
    ? ((reductionRowsField?.entries as any[]) || [])
        .map((e: any) => {
          try {
            return JSON.parse(e.value || '');
          } catch {
            return null;
          }
        })
        .filter(Boolean)
    : [];

  return {
    rows,
    zeroWorkComments: zeroWorkField?.value || '',
    partialReductionComments: partialField?.value || '',
    requestedAmount: budgetSummary?.requestedAmount != null ? Number(budgetSummary.requestedAmount) : undefined,
    recommendedAmount: budgetSummary?.totalAmountReccForFunding != null ? Number(budgetSummary.totalAmountReccForFunding) : undefined,
  };
}

/** One draw's inputs for its own §2.x application section. */
interface MagicDrawSection {
  drawId?: string;
  draw: any;
  budget: any;
  lineItems: any[];
  budgetSummaryTable: string;
  appCommentsSection?: any;
  reductions?: ExecSummaryReductions;
}

interface MagicSectionContext {
  reportData: ExportReportData;
  /** One entry per covered draw, in print order. */
  drawSections: MagicDrawSection[];
  /** Draws the Change Orders and Lien Waivers sections skip. Every other block prints them. */
  coLienHiddenDrawIds: Set<string>;
}

function buildMagicSection2Html(ctx: MagicSectionContext, majorCounter: number): { html: string; subCounter: number } {
  const { reportData, drawSections, coLienHiddenDrawIds } = ctx;
  const draw = reportData.drawSummary;
  const project = reportData.projectSummary;
  // Change Orders and Lien Waivers print only the draws the report shows; every other per-draw
  // block below keeps iterating the full set.
  const coLienDrawSections = filterVisibleCoLienDraws(drawSections, coLienHiddenDrawIds);
  let subCounter = 1;
  const parts: string[] = [];

  const fmtAmt = (n: number | undefined) => {
    if (n === undefined || n === null || !Number.isFinite(n)) return '--';
    return `$ ${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // 2.1 … 2.N — one Application for Payment section per covered draw, each consuming a sub-number.
  // The subsections below then continue from wherever this leaves off, so adding a draw
  // automatically bumps every following section number down by one.
  const applicationParts: string[] = [];
  drawSections.forEach(drawSection => {
    const { draw: sectionDraw, budget, lineItems, budgetSummaryTable, appCommentsSection, reductions } = drawSection;
    if (!budget && lineItems.length === 0) return;

    const label = `${majorCounter}.${subCounter}`;
    subCounter++;

    let appCommentsHtml = '';
    if (appCommentsSection?.fields?.length) {
      (appCommentsSection.fields as any[]).forEach((f: any) => {
        if (f.fieldType === 4 && f.entries?.length) {
          (f.entries as any[])
            .filter((e: any) => {
              if (e.isFromLastMonth) return false;
              const v = (e.value || '').trim();
              return v && v.toLowerCase() !== 'n/a';
            })
            .forEach((e: any) => {
              const safe = (e.value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />');
              appCommentsHtml += `<p style="margin: 0 0 0.5em 0; text-align: justify;">${safe}</p>`;
            });
        }
      });
    }

    const opinionHtml = (() => {
      if (!reductions) {
        return `<p style="margin: 1em 0 0.5em 0; text-align: justify;">In our opinion the amount requested is comparable to the Work completed on site based upon a Percentage of Completion Method, and the funds remaining in the overall Hard Cost Budget are adequate to complete the work. We have not reviewed the Soft Costs outlined in the Application, but leave the funding of these items to the discretion of the lender.</p>`;
      }
      const redRows = reductions.rows;
      const commentsHtml = buildReductionCommentsHtml(reductions);
      const tableHtml = buildReductionTableHtml(redRows);
      return `
        <p style="margin: 1em 0 0.5em 0; text-align: justify;"><strong>In our opinion the amount of the request is NOT comparable to the Work completed on site as explained below. The funds remaining in the overall Hard Cost Budget are adequate to complete the work assuming the recommended adjustments to the requests are made. We have not reviewed the Soft Costs outlined in the Application, but leave the funding of these items to the Lender's discretion.</strong></p>
        <p style="text-align:center; margin: 0.5em 0;"><strong>Requested: ${fmtAmt(reductions.requestedAmount)}</strong></p>
        <p style="text-align:center; margin: 0.5em 0;"><strong>Recommended: ${fmtAmt(reductions.recommendedAmount)}</strong></p>
        <p style="margin: 0.75em 0 0.5em 0; text-align: justify;">We have reduced the current funding amount for the following reasons.</p>
        ${commentsHtml}
        ${
          redRows.length > 0
            ? `
          <p style="margin: 0.75em 0 0.5em 0; text-align: justify;">Following is a list of the items reduced due to requests for funds in excess of Work observed on site:</p>
          ${tableHtml}
        `
            : ''
        }
      `;
    })();

    // With several draws the heading names the contract so each section is identifiable;
    // a single-draw report keeps the original project-named heading.
    const projectName = project?.projectTitle || project?.projectNumber || sectionDraw?.name || 'Project';
    const heading =
      drawSections.length > 1 && sectionDraw?.name ? `${projectName} — ${sectionDraw.name} Application` : `${projectName} Application`;

    applicationParts.push(`
      <div class="section-subheader">${label}. ${heading}</div>
      <p style="margin: 0 0 0.75em 0; text-align: justify;">We received a copy of the Current Application and Certificate for Payment as follows:</p>
      <p style="margin: 0 0 0.2em 0;"><strong>Application for Payment:</strong>&nbsp;${sectionDraw?.applicationNumber || '--'}</p>
      <p style="margin: 0 0 1em 0;"><strong>Period Ending Date:</strong>&nbsp;${formatDateLong(sectionDraw?.periodEnding)}</p>
      <div style="display:flex; justify-content:center; margin: 1em 0;">${budgetSummaryTable}</div>
      ${opinionHtml}
      ${appCommentsHtml}
    `);
  });
  parts.push(...applicationParts);

  // Remaining subsections — each builder returns empty string if no data
  const subsections: Array<[() => string, boolean]> = [
    [() => buildNotarySignaturesHtml(reportData.sections?.notaryAndSignatures, `${majorCounter}.${subCounter}`), true],
    // Change Orders prints one table + summary per covered draw (its comments stay section-level).
    [
      () =>
        buildChangeOrdersHtml(
          reportData.sections?.changeOrders,
          `${majorCounter}.${subCounter}`,
          coLienDrawSections.map(d => ({ drawId: d.drawId, name: d.draw?.name })),
        ),
      true,
    ],
    [
      () =>
        project?.reviewingPotentialChangeOrders === true
          ? buildPotentialChangeOrdersHtml(reportData.sections?.potentialChangeOrders, `${majorCounter}.${subCounter}`)
          : '',
      project?.reviewingPotentialChangeOrders !== true,
    ],
    // Reallocations splits its table per covered draw; its comments stay section-level.
    [
      () =>
        buildReallocationsHtml(
          reportData.sections?.reallocations,
          `${majorCounter}.${subCounter}`,
          drawSections.map(d => ({ drawId: d.drawId, name: d.draw?.name })),
        ),
      true,
    ],
    [() => buildRetainageHtml(reportData.sections?.retainage, `${majorCounter}.${subCounter}`), true],
    // Stored Materials splits its listed-materials table per covered payapp; its comments stay
    // section-level and are derived from every payapp's materials together.
    [
      () =>
        buildStoredMaterialsHtml(
          reportData.sections?.storedMaterials,
          `${majorCounter}.${subCounter}`,
          drawSections.map(d => ({ drawId: d.drawId, name: d.draw?.name })),
        ),
      true,
    ],
    // Lien Waivers prints one labelled block per covered payapp; its comment covers all of them.
    [
      () =>
        buildLienWaiversHtml(
          reportData.sections?.lienWaivers,
          `${majorCounter}.${subCounter}`,
          coLienDrawSections.map(d => ({
            drawId: d.drawId,
            name: d.draw?.name,
            applicationNumber: d.draw?.applicationNumber != null ? String(d.draw.applicationNumber) : undefined,
            periodEnding: d.draw?.periodEnding,
            periodBeginning: d.draw?.periodBeginning,
          })),
          project?.contractor,
        ),
      true,
    ],
    [() => buildConstructionScheduleHtml(reportData.sections?.schedule, `${majorCounter}.${subCounter}`), true],
    [
      () =>
        project?.reviewingConstructionMeetingMinutes === true
          ? buildConstructionMeetingMinutesHtml(reportData.sections?.constructionMeetingMinutes, `${majorCounter}.${subCounter}`)
          : '',
      project?.reviewingConstructionMeetingMinutes !== true,
    ],
    [() => buildMaterialTestingHtml(reportData.sections?.materialTestingReports, `${majorCounter}.${subCounter}`), true],
    [
      () =>
        buildPreviousFundingsTextHtml(
          (reportData as any).previousFundingsNote,
          reportData.sections?.['previous-fundings'] || (reportData.sections as any)?.previousFundings,
          `${majorCounter}.${subCounter}`,
        ),
      true,
    ],
    [() => buildDocumentsReceivedHtml(reportData.sections?.docsReceived, `${majorCounter}.${subCounter}`), true],
    [() => buildFoundationSurveyHtml(reportData.sections?.foundationSurvey, `${majorCounter}.${subCounter}`), true],
    [() => buildInitialPcrIssuesHtml(reportData.sections?.planCostReviewIssues, `${majorCounter}.${subCounter}`), true],
    // Minor Adjustments prints a labelled table per payapp over the threshold. The amounts are
    // derived from each payapp's budget summary rather than stored, so they are passed in.
    [
      () =>
        buildMinorAdjustmentsHtml(
          reportData.sections?.minorAdjustments,
          `${majorCounter}.${subCounter}`,
          drawSections.map(d => ({
            drawId: d.drawId ?? '',
            name: d.draw?.name ?? '',
            amount: Number(d.budget?.adjustmentsByCDCC) || 0,
          })),
        ),
      false,
    ],
  ];

  for (const [builder, incrementOnEmpty] of subsections) {
    const html = builder();
    if (html) {
      parts.push(html);
      subCounter++;
    } else if (!incrementOnEmpty) {
      // Always-present placeholder sections still increment the counter
      subCounter++;
    }
  }

  // Dynamic sections print one numbered subsection per user-created section (not a single shared
  // label), so it's handled separately from the uniform builders above: it needs to advance
  // subCounter by however many sections it actually rendered, not just by 1.
  const dynamicSections = buildDynamicSectionsHtml(reportData.sections?.dynamicSections, majorCounter, subCounter);
  if (dynamicSections.html) {
    parts.push(dynamicSections.html);
    subCounter += dynamicSections.count;
  }

  return { html: parts.join('<div style="margin-top: 1.5em;"></div>'), subCounter };
}

// ─── ReportExportUtil ─────────────────────────────────────────────────────────

export class ReportExportUtil {
  private getPhotos: ExportPhotosFunction;
  private getImageUrl: ExportImageUrlFunction;
  private getSignatureAsBase64: ExportSignatureFunction;
  private processPhotosForExport: (photos: any[], getImageUrl: ExportImageUrlFunction) => Promise<any[]>;

  constructor(
    getPhotos: ExportPhotosFunction,
    getImageUrl: ExportImageUrlFunction,
    getSignatureAsBase64: ExportSignatureFunction,
    processPhotosForExport: (photos: any[], getImageUrl: ExportImageUrlFunction) => Promise<any[]>,
  ) {
    this.getPhotos = getPhotos;
    this.getImageUrl = getImageUrl;
    this.getSignatureAsBase64 = getSignatureAsBase64;
    this.processPhotosForExport = processPhotosForExport;
  }

  private async convertSignatureUrlToBase64(signatureUrl?: string): Promise<string | undefined> {
    if (!signatureUrl) return undefined;
    try {
      if (signatureUrl.startsWith('data:')) return signatureUrl;
      const response = await this.getSignatureAsBase64();
      return response.dataUrl;
    } catch (error) {
      console.error('Failed to convert signature to base64:', error);
      return undefined;
    }
  }

  private async prepareHtmlWithBase64Signature(html: string, signatureUrl?: string): Promise<string> {
    if (!signatureUrl || !html.includes('alt="Signature"')) return html;
    try {
      const base64Signature = await this.convertSignatureUrlToBase64(signatureUrl);
      if (!base64Signature) return html.replace(/<img[^>]*alt="Signature"[^>]*>/g, '');
      // Legacy saved cover letters carry an inline `height:30px !important`, which beats the
      // stylesheet rule above; drop it so the export sizing wins.
      return stripSignatureInlineSize(html)
        .replace(/<img([^>]*alt="Signature"[^>]*)src="[^"]*"([^>]*)>/g, `<img$1src="${base64Signature}"$2>`)
        .replace(/<img([^>]*)src="[^"]*"([^>]*alt="Signature"[^>]*)>/g, `<img$1src="${base64Signature}"$2>`);
    } catch (error) {
      console.error('Failed to prepare HTML with base64 signature:', error);
      return html.replace(/<img[^>]*alt="Signature"[^>]*>/g, '');
    }
  }

  private prepareHtmlWithBase64Logo(html: string, logo: string = logoBase64): string {
    if (!html) return html;
    return html.replace(/<img([^>]*)(class=["']company-logo["'][^>]*)?([^>]*)>/gi, fullTag => {
      const isCompanyLogo = /class=["']company-logo["']/i.test(fullTag) || /alt=["']CD Construction Consulting["']/i.test(fullTag);
      if (!isCompanyLogo) return fullTag;
      let cleaned = fullTag.replace(/\swidth=\s*"[^"]*"/gi, '').replace(/\sheight=\s*"[^"]*"/gi, '');
      cleaned = cleaned.replace(/style=\s*"([^"]*)"/gi, (_m, styles) => {
        const filtered = styles
          .replace(/(^|;\s*)width\s*:\s*[^;]*;?/gi, ' ')
          .replace(/(^|;\s*)height\s*:\s*[^;]*;?/gi, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim();
        return filtered ? `style="${filtered}"` : '';
      });
      if (/src=\s*["'][^"']*["']/i.test(cleaned)) {
        return cleaned.replace(/src=\s*["'][^"']*["']/i, `src="${logo}"`);
      }
      return cleaned.replace(/<img/i, `<img src="${logo}"`);
    });
  }

  async buildFullReportHtml(
    reportData: ExportReportData,
    summary: ExportSummary,
    reportType: 'limited' | 'quarterly' | 'magic' = 'limited',
  ): Promise<string> {
    const draw = reportData.drawSummary;
    const budget = reportData.budgetSummary;
    const project = reportData.projectSummary;
    const lineItems = (reportData.lineItems || []).filter((li: any) => !li.isDeleted);

    // Date formatting helpers
    const formatDateLong = (value: any): string => {
      if (!value) return '--';
      // parseISO treats date-only strings (yyyy-MM-dd) as local midnight, avoiding
      // the UTC shift that causes off-by-one in US timezones.
      const date = typeof value === 'string' ? parseISO(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return '--';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Short date for recap tables; treat sentinel/empty values as blank
    const formatDateShortOrEmpty = (value: any): string => {
      if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
        return '';
      }
      return formatDateToMMDDYY(value);
    };

    // Formatting helpers to mirror legacy PDF output
    const formatNumber = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const formatCurrencyUSD = (value: any): string => {
      const num = formatNumber(value);
      if (num === null) return '--';
      const abs = Math.abs(num);
      const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return num < 0 ? `(${formatted})` : `$${formatted}`;
    };
    const formatCurrencyNoSign = (value: any): string => {
      const num = formatNumber(value);
      if (num === null) return '--';
      const abs = Math.abs(num);
      const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return num < 0 ? `(${formatted})` : formatted;
    };

    const formatPercent = (value: any): string => {
      const num = formatNumber(value);
      if (num === null) return '--';
      return `${Number(num).toFixed(2)}%`;
    };

    const formatIntegerNoSign = (value: any): string => {
      const num = formatNumber(value);
      if (num === null) return '--';
      return Math.round(num).toLocaleString('en-US');
    };

    // The draws this report covers, in print order. Reports produced before multi-draw have no
    // list, so the single drawSummary/budgetSummary/lineItems trio becomes a one-draw list and
    // every per-draw loop below runs exactly once — identical output to before.
    const coveredDraws: ExportDrawContext[] = reportData.draws?.length
      ? reportData.draws
      : draw
        ? [{ drawId: (draw as any).id, drawSummary: draw as any, budgetSummary: budget, lineItems: reportData.lineItems }]
        : [];

    // Covered draws whose Change Orders and Lien Waivers content is suppressed for this report.
    const coLienHiddenDrawIds = resolveHiddenCoLienDrawIds(
      reportData.hiddenCoLienDrawIds,
      coveredDraws.map(d => d.drawId),
    );

    /** This draw's reduction-comments section, whether stored per draw or (legacy) unscoped. */
    const reductionSectionForDraw = (drawId?: string): any => {
      const sections = reportData.sections as any;
      if (drawId) {
        const scoped = sections?.[drawScopedKey('reduction-comments', drawId)];
        if (scoped) return scoped;
      }
      return sections?.['reduction-comments'] ?? sections?.reductionComments;
    };

    const applicationSectionForDraw = (drawId?: string): any => {
      const sections = reportData.sections as any;
      if (drawId) {
        const scoped = sections?.[drawScopedKey('application-comments', drawId)];
        if (scoped) return scoped;
      }
      return sections?.['application-comments'] ?? sections?.applicationComments;
    };

    // Per-draw reductions, used for the §1.1 blocks (one per draw with reductions) and each
    // draw's own §2.x opinion paragraph.
    const drawReductions: DrawReductions[] = coveredDraws
      .map(coveredDraw => {
        const reductions = extractDrawReductions(reductionSectionForDraw(coveredDraw.drawId), coveredDraw.budgetSummary, reportType);
        if (!reductions) return null;
        return {
          ...reductions,
          drawId: coveredDraw.drawId,
          contractName: coveredDraw.drawSummary?.name || '[draw name]',
          contractor: project?.contractor || '[contractor]',
        } as DrawReductions;
      })
      .filter((r): r is DrawReductions => r !== null);

    const hasReductions = drawReductions.length > 0;
    // Primary draw's reductions, for the blocks that are still single-draw.
    const execSummaryReductions: ExecSummaryReductions | undefined =
      drawReductions.find(r => r.drawId === coveredDraws[coveredDraws.length - 1]?.drawId) ?? drawReductions[0];

    // Export-time fallback templates (pass reductions so the default HTML is correct
    // even when the user has never saved the cover letter / executive summary).
    const defaultExecutiveHtml = buildExecutiveSummaryHTML(reportData, reportType as 'limited' | 'magic', drawReductions);
    const defaultCoverHtml = buildCoverPageHTML(reportData, hasReductions);

    // ── Cover page ────────────────────────────────────────────────────────────
    let magicCoverPageHtml = '';
    if (reportType === 'magic') {
      const coverPagePhotoIds = reportData.coverPagePhotoIds || reportData.coverPage?.coverPagePhotoIds || [];
      let coverPagePhotos: any[] | undefined;

      if (coverPagePhotoIds.length > 0 && reportData.reportId) {
        try {
          const allPhotos = await this.getPhotos(reportData.reportId);
          const selected = coverPagePhotoIds
            .map((id: string) => allPhotos.find((p: ReportPhotoDTO) => p.id === id))
            .filter((p: ReportPhotoDTO | undefined): p is ReportPhotoDTO => p !== undefined);

          coverPagePhotos = await Promise.all(
            selected.map(async (photo: any) => {
              const blobName = photo.pdfBlobName || photo.blobName || photo.originalBlobName;
              return { url: await this.getImageUrl(blobName), caption: photo.caption };
            }),
          );
        } catch (error) {
          console.error('Failed to load cover page photos for export:', error);
        }
      }

      const tableData: CoverPageTableData = {
        budgetSummary: budget,
        drawSummary: draw,
        coverPage: reportData.coverPage,
        scheduleSection: reportData.sections?.schedule,
        changeOrdersSection: reportData.sections?.changeOrders,
        lienWaiversSection: reportData.sections?.lienWaivers,
        reallocationsSection: reportData.sections?.reallocations,
        storedMaterialsSection: reportData.sections?.storedMaterials,
        siteVisitDate: reportData.siteConditions?.inspectionDate,
        lineItems,
        // Drives the multi-payapp form of the table (summed amounts, weighted percentages,
        // "Total …" labels). One covered draw leaves the table exactly as it was.
        draws: coveredDraws,
        // The change-order and lien-release cells aggregate across payapps, so they have to skip
        // the ones whose sections the report hides.
        hiddenCoLienDrawIds: Array.from(coLienHiddenDrawIds),
        scheduleGraph: reportData.sections?.scheduleGraph,
        scheduleGraphAxis: reportData.sections?.scheduleGraphAxis,
      };

      magicCoverPageHtml = `<div class="MuiPaper-root">${this.prepareHtmlWithBase64Logo(
        buildMagicReportCoverPageHTML(reportData, coverPagePhotos, tableData),
      )}</div>`;
    }

    // ── Cover letter ──────────────────────────────────────────────────────────
    const rawCoverContent = summary.coverLetter || defaultCoverHtml;
    // When reductions exist, replace the "same amount" paragraph even in user-saved cover letter HTML.
    const coverContentMaybeReductions = hasReductions
      ? rawCoverContent.replace(
          /<p[^>]*>(?:<[^>]+>)*This is the same amount[\s\S]*?<\/p>/gi,
          `<p><strong>Please note that this amount is less than the amount requested by the Borrower. Refer to the report below for details.</strong></p>`,
        )
      : rawCoverContent;
    const normalizedCoverContent = coverContentMaybeReductions.replace(
      /<p[^>]*>CD Construction Consulting<\/p>/gi,
      '<p class="signature-company" style="margin: 0;">CD Construction Consulting</p>',
    );
    let coverPageHtmlWithWrapper = `<div class="MuiPaper-root">${normalizedCoverContent}</div>`;
    if (reportData.author?.signatureUrl) {
      coverPageHtmlWithWrapper = await this.prepareHtmlWithBase64Signature(coverPageHtmlWithWrapper, reportData.author.signatureUrl);
    }
    coverPageHtmlWithWrapper = this.prepareHtmlWithBase64Logo(coverPageHtmlWithWrapper);

    // ── Executive summary ─────────────────────────────────────────────────────
    // When reductions exist and the user has a saved executive summary, replace the
    // "In our opinion…" paragraph with the full reductions block.
    const execSummaryRawHtml = (() => {
      const base = summary.executiveSummary || defaultExecutiveHtml;
      if (!hasReductions || !summary.executiveSummary) return base;
      // One block per draw with reductions, so nothing entered in a draw's tab goes unprinted.
      const reductionsBlock = buildAllReductionBlocksHtml(drawReductions);
      return base.replace(/<p[^>]*>(?:<[^>]+>)*In our opinion[\s\S]*?<\/p>/gi, reductionsBlock);
    })();

    // Re-derive the Project Description block from current section data and splice it in fresh —
    // this keeps it correct even when `summary.executiveSummary` is HTML saved before this field
    // existed (or edited since), rather than relying on whatever the saved HTML happens to contain.
    const projectDescriptionBlock = buildProjectDescriptionHtml(
      (reportData.sections as any)?.projectDescription?.fields?.find((f: any) => f.fieldName === 'ProjectDescriptionText')?.value,
    );
    const execSummaryHtmlWithProjectDescription = execSummaryRawHtml
      .replace(/<div class="project-description-block">[\s\S]*?<\/div>\s*/, '')
      .replace(/<h2 class="executive-summary-heading"/, `${projectDescriptionBlock}<h2 class="executive-summary-heading"`);

    const executiveSummaryHtml = `
      <div class="MuiPaper-root">
        <div class="exec-summary">
          ${this.prepareHtmlWithBase64Logo(execSummaryHtmlWithProjectDescription)}
        </div>
      </div>
    `;

    // ── Shared data ───────────────────────────────────────────────────────────
    const previousFundingsTableHead = `
      <thead>
        <tr><th colspan="3" class="pf-header-main">SUMMARY OF PREVIOUS FUNDINGS:</th></tr>
        <tr>
          <th class="pf-subheader pf-subheader-appno">Application #:</th>
          <th class="pf-subheader pf-subheader-right">Appl. Date:</th>
          <th class="pf-subheader pf-subheader-right">Amount Funded:</th>
        </tr>
      </thead>
    `;

    /** Everything one draw contributes to the printed report: its budget pages and its §2.x section. */
    const buildDrawArtifacts = (coveredDraw: ExportDrawContext) => {
      const drawSummary = coveredDraw.drawSummary;
      const drawBudget = coveredDraw.budgetSummary;
      const drawLineItems = (coveredDraw.lineItems || []).filter((li: any) => !li.isDeleted);

      const drawBudgetSummaryTable = drawBudget ? buildBudgetSummaryTable(drawBudget) : '';
      const drawSummaryTable = drawBudget || drawLineItems.length > 0 ? buildDrawSummaryTable(project, drawSummary) : '';

      const sortedPreviousFundings = [...(drawSummary?.previousFundings || [])].sort(
        (a: any, b: any) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0),
      );
      const previousFundingRowItems = sortedPreviousFundings.map(
        (pf: any) => `
        <tr class="pf-row">
          <td class="pf-cell-appno">${pf.reviewedByCDCC === false ? '<span class="pf-asterisk">*</span>&nbsp;' : ''}${pf.applicationNumber ?? ''}</td>
          <td class="pf-cell pf-cell-right">${formatDateShortOrEmpty(pf.applicationDate)}</td>
          <td class="pf-cell pf-cell-right">${formatCurrencyNoSign(pf.amountFunded)}</td>
        </tr>
      `,
      );
      const previousFundingsTotal = sortedPreviousFundings.reduce((sum: number, pf: any) => sum + (Number(pf.amountFunded) || 0), 0);
      const hasUnreviewedFunding = sortedPreviousFundings.some((pf: any) => pf.reviewedByCDCC === false);

      const budgetPagesCtx: BudgetPagesContext = {
        budget: drawBudget,
        draw: drawSummary,
        project,
        lineItems: drawLineItems,
        budgetSummaryTable: drawBudgetSummaryTable,
        drawSummaryTable,
        previousFundingRowItems,
        previousFundingsTotal,
        previousFundingsTableHead,
        hasUnreviewedFunding,
        lineItemRows: buildLineItemRows(drawLineItems),
        lineItemTotalsRow: drawLineItems.length > 0 ? buildLineItemTotalsRow(drawLineItems) : '',
      };

      const drawSection: MagicDrawSection = {
        drawId: coveredDraw.drawId,
        draw: drawSummary,
        budget: drawBudget,
        lineItems: drawLineItems,
        budgetSummaryTable: drawBudgetSummaryTable,
        appCommentsSection: applicationSectionForDraw(coveredDraw.drawId),
        reductions: drawReductions.find(r => r.drawId === coveredDraw.drawId),
      };

      return { budgetPagesCtx, drawSection };
    };

    const drawArtifacts = coveredDraws.map(buildDrawArtifacts);
    const drawSections = drawArtifacts.map(a => a.drawSection);
    const budgetPageCtxs = drawArtifacts.map(a => a.budgetPagesCtx);
    // Primary draw's tables, for the blocks that are still single-draw.
    const primaryArtifacts = drawArtifacts[drawArtifacts.length - 1];
    const budgetSummaryTable = primaryArtifacts?.budgetPagesCtx.budgetSummaryTable ?? '';

    // ── Recap data ────────────────────────────────────────────────────────────
    const lienWaiverHistoryField = reportData.sections?.lienWaivers?.fields?.find((f: any) => f.fieldName === 'LienWaiverHistory');
    const lienWaiverHistory: any[] = (() => {
      try {
        const parsed = JSON.parse(lienWaiverHistoryField?.value || '');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })();
    // Grouped by payapp in coverage order, then chronologically within each payapp. Rows written
    // before multi-draw carry no drawId and are attributed to the primary payapp. Payapps whose
    // Lien Waivers block is hidden are left out — they have no visible section to recap, and no
    // new rows are being written for them either.
    const sortedHistory = sortLienWaiverHistory(
      lienWaiverHistory,
      filterVisibleCoLienDraws(coveredDraws, coLienHiddenDrawIds)
        .map(d => d.drawId)
        .filter((id): id is string => Boolean(id)),
    );

    const recapRows = sortedHistory
      .filter(entry => entry?.status && entry.status !== 'Not Present')
      .map(
        entry => `
        <tr>
          <td>${entry.contractor || entry.drawName || project?.contractor || draw?.name || '--'}</td>
          <td>${formatDateShortOrEmpty(entry.applicationDate)}</td>
          <td>${entry.applicationNumber || '--'}</td>
          <td>${entry.status === 'Conditional' ? 'Conditional Waiver' : entry.amount || '--'}</td>
        </tr>
      `,
      )
      .join('');

    // Recap of Previous Applications: one table per G702, in the order each first appears, titled
    // and formatted as on the Excel Report tab. Sourced only from the stored recap, never from the
    // draws' previous fundings.
    const contractorName = project?.contractor || draw?.name || 'Contractor';
    const projectTitle = project?.projectTitle || project?.projectNumber || draw?.name || 'Project';
    const recapApplicationGroups = new Map<string, any[]>();
    [...(reportData.recapApplications || [])]
      .sort((a: any, b: any) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
      .forEach((application: any) => {
        const key = application?.projectDrawId || '';
        const group = recapApplicationGroups.get(key);
        if (group) group.push(application);
        else recapApplicationGroups.set(key, [application]);
      });
    // The Report tab prints retainage as 0.0% and the completion columns as whole percents.
    const formatRecapPercent = (value: any, decimals: number): string => {
      const num = formatNumber(value);
      return num === null ? '--' : `${Number(num).toFixed(decimals)}%`;
    };
    const recapApplicationRows = (applications: any[]) =>
      applications
        .map(
          (application: any) => `
        <tr>
          <td>${application.applicationNumber || '--'}</td>
          <td>${formatDateShortOrEmpty(application.applicationDate)}</td>
          <td>${formatCurrencyUSD(application.requestedAmount)}</td>
          <td>${formatCurrencyUSD(application.approvedAmount)}</td>
          <td>${formatRecapPercent(application.retainage, 1)}</td>
          <td>${formatRecapPercent(application.percentRequested, 0)}</td>
          <td>${formatRecapPercent(application.percentComplete, 0)}</td>
          <td>${formatCurrencyUSD(application.contractAmount)}</td>
        </tr>
      `,
        )
        .join('');
    const recapApplicationsTables = recapApplicationGroups.size
      ? Array.from(recapApplicationGroups.values())
          .map(applications => {
            const first = applications[0];
            const heading = (first?.heading || '').trim() || `${contractorName}'s ${projectTitle} Application:`;
            return buildRecapApplicationsTable(heading, recapApplicationRows(applications));
          })
          .join('')
      : buildRecapApplicationsTable(`${contractorName}'s ${projectTitle} Application:`, '');

    const recapTable = buildRecapTable(
      recapApplicationsTables,
      recapRows,
      `${contractorName}'s ${projectTitle} Lien Waivers`,
      sortedHistory.some((e: any) => e?.status === 'Conditional'),
    );

    // ── Photos ────────────────────────────────────────────────────────────────
    const photos = reportData.reportId ? await this.getPhotos(reportData.reportId) : [];
    const processedPhotos = await this.processPhotosForExport(photos, this.getImageUrl);
    const PHOTOS_PER_PAGE = 6;
    // Photos page header, line 1: project name + Site Conditions inspection date.
    const photosHeaderLine1 = buildPhotosHeaderLine1(
      project?.projectTitle || project?.projectNumber || '',
      reportData.siteConditions?.inspectionDate,
    );

    // Action Items (Section 1) — kept on the same page as the Executive Summary above,
    // so it continues directly under it instead of starting a new page.
    const actionItemsHtml = summary.actionItems ? `<div class="action-items">${summary.actionItems}</div>` : '';

    // ── Assemble body ─────────────────────────────────────────────────────────
    let bodyHtml: string;

    if (reportType === 'magic') {
      let majorCounter = 2;
      const allMagicPages: string[] = [];

      // Section 2: Report — one 2.x application section per covered draw, then the rest.
      const { html: section2BodyHtml } = buildMagicSection2Html({ reportData, drawSections, coLienHiddenDrawIds }, majorCounter);

      // Section 3: Construction Progress (optional) — no page break before it, appended to section 2
      const progressHtml = buildProgressDescriptionHtml(reportData.sections?.progressDescription, `${majorCounter + 1}`);

      const majorHeaderHtml = `<div class="major-section-header">${majorCounter}. REPORT</div>`;
      majorCounter++;

      if (progressHtml) {
        majorCounter++;
      }

      // Section N: Recap ("Previous Reports")
      const recapHtml = `<div class="major-section-header">${majorCounter}. RECAP OF PREVIOUS REPORTS</div>
           <div class="section-subheader">${majorCounter}.1. Previous Applications for Payment</div>
           ${recapTable}`;
      majorCounter++;

      // Section N: Report Qualifications
      const reportQualificationsHtml = `<div class="MuiPaper-root">${buildReportQualificationsPart1Html(majorCounter)}${buildReportQualificationsPart2Html(majorCounter)}</div>`;

      // Sections 2 (Report), 3 (Progress), 4 (Recap), and 5 (Report Qualifications)
      // are all combined into a single `.page` so no hard page break falls between any of them.
      // Each section is a flat, page-root-level sibling (not nested inside a shared wrapper) —
      // the editor's page-split logic reconstructs overflow content one nesting level at a
      // time, and mixed-depth nesting here previously scrambled section order across pages.
      allMagicPages.push(
        buildPage(
          'section-page recap report-qualifications',
          `${majorHeaderHtml}<div class="MuiPaper-root">${section2BodyHtml}</div>${
            progressHtml ? `<br /><div class="MuiPaper-root">${progressHtml}</div>` : ''
          }<br />${recapHtml}<br />${reportQualificationsHtml}`,
        ),
      );

      // Photographs
      allMagicPages.push(buildPhotosPages(processedPhotos, PHOTOS_PER_PAGE, photosHeaderLine1));

      // Budget + Continuation — one pair of pages per covered draw, in draw order
      budgetPageCtxs.forEach(ctx => allMagicPages.push(...buildBudgetPages(ctx)));

      bodyHtml = allMagicPages.join('');
    } else {
      // Limited / Quarterly
      const parts: string[] = [];
      parts.push(
        buildPage('limitations', `<div class="MuiPaper-root"><div class="limitations-content">${LIMITATIONS_DISCLOSURES_HTML}</div></div>`),
      );
      parts.push(buildPhotosPages(processedPhotos, PHOTOS_PER_PAGE, photosHeaderLine1));
      budgetPageCtxs.forEach(ctx => parts.push(...buildBudgetPages(ctx)));
      bodyHtml = parts.join('');
    }

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>${REPORT_STYLES}</style>
</head>
<body>
  ${reportType === 'magic' ? buildPage('magic-cover-page', magicCoverPageHtml) : ''}
  ${buildPage('cover-letter', coverPageHtmlWithWrapper)}
  ${buildPage(
    'executive-summary',
    `
    ${executiveSummaryHtml}
    ${actionItemsHtml}
  `,
  )}
  ${bodyHtml}
</body>
</html>`;
  }

  async buildPhotosOnlyHtml(reportId: string, options?: { projectName?: string; date?: Date }): Promise<string> {
    const photos = await this.getPhotos(reportId);
    const processedPhotos = await this.processPhotosForExport(photos, this.getImageUrl);

    const formattedDate =
      options?.date instanceof Date ? options.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
    const headerLine1 =
      options?.projectName && formattedDate ? `${options.projectName}, ${formattedDate}` : options?.projectName || formattedDate || '';
    const headerHtml = `<div class="photos-header"><div class="photos-header-line1">${headerLine1}</div><div class="photos-header-line2">PHOTOGRAPHS</div></div>`;

    const PHOTOS_PER_PAGE = 6;
    const styles = `
      @page { size: 210mm 297mm; }
      html, body { background: white; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { break-after: page; background: white; margin: 0; padding: 0; }
      .page:last-child { break-after: avoid; }
      .photos-header { margin: 0 0 32px 0; }
      .photos-header-line1 { font-size: 16px; font-weight: bold; color: #000; text-align: center; margin: 0 0 4px 0; }
      .photos-header-line2 { color: #1976d2; font-weight: bold; font-size: 25px; text-align: center; }
      .photos-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 10px; row-gap: 0; }
      .photo { display: flex; flex-direction: column; align-items: center; }
      .photo .image-frame { height: 228px; width: 319px; display: flex; align-items: center; justify-content: center; }
      .photo img { width: 319px; height: 228px; object-fit: cover; display: block; }
      .photo .caption { min-height: 72px; width: 100%; font-size: 16px; color: #333; display: flex; align-items: center; justify-content: center; text-align: center; padding: 0 10px; box-sizing: border-box; }
      .photo .caption * { text-align: center; }
    `;

    const pagesHtml =
      processedPhotos.length > 0
        ? Array.from({ length: Math.ceil(processedPhotos.length / PHOTOS_PER_PAGE) }, (_, i) => {
            const pagePhotos = processedPhotos.slice(i * PHOTOS_PER_PAGE, (i + 1) * PHOTOS_PER_PAGE);
            const grid = pagePhotos
              .map(
                (p: any) => `
                  <div class="photo">
                    <div class="image-frame"><img src="${p.url}" /></div>
                    <div class="caption">${p.caption ?? ''}</div>
                  </div>`,
              )
              .join('');
            const header = i === 0 ? headerHtml : '';
            return `<div class="page">${header}<div class="photos-grid">${grid}</div></div>`;
          }).join('')
        : `<div class="page">${headerHtml}<p style="text-align:center;">No photos.</p></div>`;

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>${styles}</style>
</head>
<body>${pagesHtml}</body>
</html>`;
  }
}
